import React, { ReactElement, useState, useCallback } from 'react';
import { Box, Typography, FormHelperText } from '@mui/material';
import Dropzone from 'react-dropzone';
import { CabComponentProps } from '../cabStyled';
import colors from '../../../colors';
import { ALLOWED_IMG_EXTS } from '../../../constants';
import { CabAvatar } from '../CabAvatar';
import { CabIcon } from '../CabIcon';
import { IoCloudUploadOutline, IoCloseCircle } from 'react-icons/io5';

export interface CabImageUploadProps extends CabComponentProps<object> {
  // Image data
  currentImage?: string | null;
  currentImageName?: string;
  
  // Upload configuration
  maxFileSize?: number; // in bytes, default 2.5MB
  acceptedFileTypes?: string[]; // default ALLOWED_IMG_EXTS
  useDropzone?: boolean; // default true, false for file input only
  
  // Display configuration
  displayMode?: 'avatar' | 'logo' | 'custom';
  avatarSize?: 'large' | 'intermediate' | 'medium' | 'small';
  avatarName?: string;
  avatarColor?: string;
  logoMaxWidth?: number;
  logoMaxHeight?: number;
  customPreview?: React.ReactNode;
  
  // Interaction configuration
  editable?: boolean; // default true
  showRemoveButton?: boolean; // default false
  showUploadIcon?: boolean; // default true for dropzone mode
  
  // Callbacks
  onImageSelect?: (file: File) => void;
  onImageRemove?: () => void;
  onError?: (error: string) => void;
  
  // Styling
  dropzoneSx?: React.CSSProperties;
  previewSx?: React.CSSProperties;
  errorSx?: React.CSSProperties;
  
  // Labels
  dragDropText?: string;
  clickText?: string;
  fileTypeText?: string;
  maxSizeText?: string;
  removeButtonText?: string;
}

export const CabImageUpload = ({
  currentImage,
  currentImageName,
  maxFileSize = 2500000, // 2.5MB
  acceptedFileTypes = ALLOWED_IMG_EXTS,
  useDropzone = true,
  displayMode = 'avatar',
  avatarSize = 'large',
  avatarName = '',
  avatarColor = colors.black500,
  logoMaxWidth = 200,
  logoMaxHeight,
  customPreview,
  editable = true,
  showRemoveButton = false,
  showUploadIcon = true,
  onImageSelect,
  onImageRemove,
  onError,
  dropzoneSx,
  previewSx,
  errorSx,
  dragDropText = 'Drag & drop file or',
  clickText = 'click',
  fileTypeText = 'JPG or PNG. Max',
  maxSizeText = '2.5Mb',
  removeButtonText = 'Remove Photo',
  sx,
  ...props
}: CabImageUploadProps): ReactElement => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [currentImageRemoved, setCurrentImageRemoved] = useState(false);

  const validateFile = useCallback((file: File): boolean => {
    if (file.size > maxFileSize) {
      const error = `Max file size is ${(maxFileSize / 1000000).toFixed(1)}Mb`;
      setErrorMessage(error);
      onError?.(error);
      return false;
    }
    
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!acceptedFileTypes.includes(fileExtension)) {
      const error = `Supported file types include ${acceptedFileTypes.join(' & ')}`;
      setErrorMessage(error);
      onError?.(error);
      return false;
    }
    
    setErrorMessage(null);
    return true;
  }, [maxFileSize, acceptedFileTypes, onError]);

  const handleFileSelect = useCallback((file: File) => {
    if (validateFile(file)) {
      setPreviewUrl(URL.createObjectURL(file));
      setCurrentImageRemoved(false);
      onImageSelect?.(file);
    }
  }, [validateFile, onImageSelect]);

  const handleRemove = useCallback(() => {
    setPreviewUrl(null);
    setErrorMessage(null);
    setCurrentImageRemoved(true);
    onImageRemove?.();
  }, [onImageRemove]);

  const renderPreview = () => {
    const imageUrl = previewUrl || (currentImageRemoved ? null : currentImage);
    
    if (displayMode === 'avatar') {
      return (
        <CabAvatar
          name={avatarName}
          src={imageUrl}
          color={avatarColor}
          size={avatarSize}
          sx={previewSx}
        />
      );
    }
    
    if (displayMode === 'logo') {
      return imageUrl ? (
        <Box
          component='img'
          src={imageUrl}
          alt={currentImageName || 'logo'}
          sx={{
            maxWidth: logoMaxWidth,
            maxHeight: logoMaxHeight,
            height: 'auto',
            border: `1px solid ${colors.black200}`,
            padding: 1,
            borderRadius: '6px',
            backgroundSize: '20px 20px',
            backgroundColor: colors.greyBackdrop,
            ...previewSx
          }} />
      ) : (
        <Box
          sx={{
            width: 117,
            height: 117,
            background: '#676767',
            border: '1px solid #3A4E64',
            borderRadius: '5px',
            ...previewSx
          }}
        />
      );
    }
    
    if (displayMode === 'custom') {
      return customPreview;
    }
    
    return null;
  };

  const renderDropzone = () => (
    <Dropzone
      onDrop={(acceptedFiles) => acceptedFiles[0] && handleFileSelect(acceptedFiles[0])}
      accept={{ "": acceptedFileTypes }}
      useFsAccessApi={false}
      disabled={!editable}
    >
      {({ getRootProps, getInputProps }) => (
        <Box
          {...getRootProps()}
          sx={{
            display: 'flex',
            cursor: editable ? 'pointer' : 'default',
            ...dropzoneSx
          }}>
          <Box sx={{
            marginRight: 2
          }}>
            {renderPreview()}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box sx={{ textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <input {...getInputProps()} />
              <Box sx={{ cursor: editable ? 'pointer' : 'default' }}>
                <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: '15px' }}>
                  {dragDropText} <span style={{ textDecoration: 'underline' }}>{clickText}</span>
                </Typography>
                <Typography variant="body2" color={colors.black700} sx={{
                  lineHeight: '30px'
                }}>
                  {fileTypeText} {maxSizeText}
                </Typography>
              </Box>
              {errorMessage && (
                <FormHelperText error sx={errorSx}>
                  Error: {errorMessage}
                </FormHelperText>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Dropzone>
  );

  const renderFileInput = () => (
    <Box sx={{ position: 'relative' }}>
      <input
        type="file"
        accept={acceptedFileTypes.join(',')}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFileSelect(file);
        }}
        style={{ display: 'none' }}
        id="image-upload-input"
      />
      <Box
        onClick={() => editable && document.getElementById('image-upload-input')?.click()}
        sx={{
          cursor: editable ? 'pointer' : 'default',
          position: 'relative',
          display: 'inline-block'
        }}
      >
        {renderPreview()}
        {showUploadIcon && editable && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              opacity: 0,
              transition: 'opacity 0.2s',
              '&:hover': { opacity: 1 }
            }}
          >
            <CabIcon Icon={IoCloudUploadOutline} sx={{ fontSize: 42, color: colors.white900 }} />
          </Box>
        )}
      </Box>
      {errorMessage && (
        <FormHelperText error sx={errorSx}>
          Error: {errorMessage}
        </FormHelperText>
      )}
    </Box>
  );

  const renderRemoveButton = () => {
    if (!showRemoveButton || !editable) return null;
    
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          marginTop: 1
        }}>
        <Box
          component="button"
          onClick={handleRemove}
          disabled={(!currentImage || currentImageRemoved) && !previewUrl}
          sx={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: colors.purplePrimary,
            fontSize: '0.875rem',
            '&:disabled': {
              color: colors.black400,
              cursor: 'not-allowed'
            }
          }}
        >
          <CabIcon Icon={IoCloseCircle} />
          {removeButtonText}
        </Box>
      </Box>
    );
  };

  if (!editable) {
    return (
      <Box sx={sx} {...props}>
        <Box sx={{
          marginRight: 2
        }}>
          {renderPreview()}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={sx} {...props}>
      {useDropzone ? renderDropzone() : renderFileInput()}
      {renderRemoveButton()}
    </Box>
  );
};

export default CabImageUpload; 